import React from "react";

const Table = ({
  columns,
  data,
  onRowClick = null,
  getTableProps,
  getTableBodyProps,
  headerGroups,
  prepareRow,
  canPreviousPage,
  canNextPage,
  previousPage,
  nextPage,
  pageSize,
  pageIndex, // ← passed down from useTable state
  setPageSize,
  totalFilteredRows,
}) => {
  /* ──────────────────────────────────────────
     Calculate the first & last row numbers
     ────────────────────────────────────────── */

  // fallback to safe numeric values so the math never yields NaN
  const safeIndex = Number.isFinite(pageIndex) ? pageIndex : 0;
  const safeSize = Number.isFinite(pageSize) ? pageSize : data.length || 1;

  const firstRow = totalFilteredRows === 0 ? 0 : safeIndex * safeSize + 1;

  const lastRow =
    totalFilteredRows === 0
      ? 0
      : Math.min(safeIndex * safeSize + data.length, totalFilteredRows);

  return (
    <div className="mt-4">
      <div className="overflow-auto max-h-[calc(100vh-28vh)] min-h-[calc(100vh-28vh)] shadow shadow-black rounded-lg">
        <table className="table-auto min-w-full" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, columnIndex) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={`px-4 py-2 bg-joule text-white sticky top-0 z-10 text-left ${
                      columnIndex === 0 ? "rounded-tl-lg" : ""
                    } ${
                      columnIndex === headerGroup.headers.length - 1
                        ? "rounded-tr-lg"
                        : ""
                    }`}
                  >
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {data.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() =>
                    onRowClick &&
                    typeof onRowClick === "function" &&
                    onRowClick(row)
                  }
                  className={`hover:bg-gray-200 ${
                    row.getRowProps().className || ""
                  }`}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className="px-4 py-2 text-left"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="pagination flex items-center justify-between mt-4">
        {/* Prev / Next */}
        <div className="flex items-center">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className={`mr-2 px-4 py-2 rounded border ${
              canPreviousPage
                ? "hover:bg-gray-200"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            Previous
          </button>
          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className={`px-4 py-2 rounded border ${
              canNextPage
                ? "hover:bg-gray-200"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            Next
          </button>
        </div>

        {/* Row-range display */}
        <div className="text-black font-semibold">
          Showing rows {firstRow}-{lastRow} out&nbsp;of {totalFilteredRows} rows
        </div>

        {/* Rows-per-page selector */}
        <div className="flex items-center">
          <label htmlFor="rowsPerPage" className="mr-2">
            Rows per page:
          </label>
          <select
            id="rowsPerPage"
            className="border rounded p-2"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default Table;
